import { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ObjectSchema, mixed, number, object, string } from "yup";
import { AvisConstants } from "../../../../data/avisConstants";
import {
  CompletedLicenseBriefInfo,
  LicenseBriefInfo,
  LicenseListFilter,
} from "../../../../generated/data-contracts";
import { FilterType, ILinkValue } from "../../../../models";
import {
  IAxiosError,
  LicenseesUrls,
  LicenseesUtils,
  TableUtils,
  api,
} from "../../../../utils";
import { AvisForm } from "../../../shared/components";
import { AvisDescriptiveText } from "../../../shared/components/form";
import {
  AvisFilterableTable,
  IColumnConfig,
  ITableColumnProps,
} from "../../../shared/components/table";
import { AvisContext } from "../../../shared/context/AvisContext";
import { LicensesFilterForm } from "./filter/LicensesFilterForm";

interface ILicensesPageProps {
  textKey: string;
  completed?: boolean;
}
export function LicensesPage(props: Readonly<ILicensesPageProps>) {
  const { textKey, completed } = props;
  const { res } = useContext(AvisContext);
  const navigate = useNavigate();
  const [error, setError] = useState<IAxiosError>();
  const defaultFilterValues: LicenseListFilter = {
    licenseNumberFilterType: "Contains",
    licenseNumber: "",
    licenseObjectNameFilterType: "Contains",
    licenseObjectName: "",
    pageIndex: 0,
    pageSize: 10,
  };

  const validationSchema: ObjectSchema<LicenseListFilter> = object({
    licenseNumber: string(),
    licenseNumberFilterType: mixed<FilterType>(),
    licenseObjectName: string(),
    licenseObjectNameFilterType: mixed<FilterType>(),
    pageIndex: number().min(0),
    pageSize: number().min(1),
  });

  const columnMetadata: IColumnConfig[] = useMemo(() => {
    const licenseObjectColumnTemplate = (
      rowData: LicenseBriefInfo | CompletedLicenseBriefInfo,
      column: ITableColumnProps,
    ): JSX.Element => {
      if (!column.field || !rowData.licenseHeaderId) return <></>;

      if (rowData.isGroupingLicense) {
        const childLicensesPageUrl = LicenseesUtils.getUrlWithParams(
          LicenseesUrls.licenseeLicenseViewChildLicenses,
          { licenseId: rowData.licenseHeaderId },
        );
        const objectTitle = res("licensees_License_label_ObjectList");
        return (
          <div className="license-object-list">
            <Link
              to={childLicensesPageUrl}
              title={objectTitle}
              content={objectTitle}
            >
              {objectTitle}
            </Link>
          </div>
        );
      }

      if (
        !rowData.objects ||
        !Array.isArray(rowData.objects) ||
        rowData.objects.length === 0
      )
        return <></>;

      const objectsCollection = rowData.objects.map((x, i) => {
        const objectValue = LicenseesUtils.getLicenseObjectValue(x);
        return (
          <div
            key={`lid_${rowData.licenseHeaderId}_object_${i}`}
            className="license-object-record table-cell-value-overflow"
            title={objectValue}
          >
            {objectValue}
          </div>
        );
      });

      return <div>{objectsCollection}</div>;
    };

    return [
      TableUtils.columnConfigLink<LicenseBriefInfo | CompletedLicenseBriefInfo>(
        "licenseNumber",
        res("licensees_License_label_Number"),
        (rowData) => {
          if (rowData.isGroupingLicense) return undefined;

          return {
            title: rowData.licenseNumber,
            url: LicenseesUtils.getUrlWithParams(
              LicenseesUrls.licenseeLicenseView,
              { licenseId: rowData.licenseHeaderId },
            ),
          } as ILinkValue;
        },
        AvisConstants.table_column_license_number_width,
      ),
      TableUtils.columnConfigPeriod<
        LicenseBriefInfo | CompletedLicenseBriefInfo
      >(
        "dateFrom",
        "dateTo",
        res("licensees_License_label_Period"),
        undefined,
        AvisConstants.table_column_period_width,
      ),
      TableUtils.columnConfig<LicenseBriefInfo | CompletedLicenseBriefInfo>(
        "objects",
        res("licensees_License_label_ObjectName"),
        "Object",
        licenseObjectColumnTemplate,
      ),
      ...(!completed
        ? [
            TableUtils.columnConfigString<LicenseBriefInfo>(
              "documentStatusName",
              res("label_Status"),
              undefined,
              150,
            ),
            TableUtils.columnConfigMoney<LicenseBriefInfo>(
              "invoiceSum",
              res("licensees_License_label_InvoiceSum"),
              (rowData) =>
                LicenseesUtils.getUrlWithParams(
                  LicenseesUrls.licenseeLicenseViewInvoices,
                  { licenseId: rowData.licenseHeaderId },
                ),
              (navigateTo) => navigate(navigateTo),
              100,
            ),
          ]
        : []),
    ];
  }, [res, completed, navigate]);

  const renderContent = (
    <>
      <AvisDescriptiveText textKey={textKey} />
      <AvisFilterableTable<
        LicenseBriefInfo | CompletedLicenseBriefInfo,
        LicenseListFilter
      >
        defaultFilterValues={defaultFilterValues}
        filterSessionKey={AvisConstants.licenses_filter_values_key}
        filterValidationSchema={validationSchema}
        setError={setError}
        getApiData={(filter) => {
          return completed
            ? api.licenseesGetCompletedLicenses(filter as any)
            : api.licenseesGetActiveLicenses(filter as any);
        }}
        renderFilter={(bag, loading, filtered) => (
          <LicensesFilterForm bag={bag} loading={loading} filtered={filtered} />
        )}
        tableColumns={columnMetadata}
        tableColumnWidths={completed ? [15, 20, 65] : [15, 15, 45, 8, 15]}
      />
    </>
  );

  const pageTitle = useMemo(
    () =>
      completed
        ? res("licensees_Licenses_PageTitle_completed")
        : res("licensees_Licenses_PageTitle_actual"),
    [res, completed],
  );

  return (
    <AvisForm title={pageTitle} error={error}>
      {renderContent}
    </AvisForm>
  );
}
